// Serien-Eckdaten eines Dauerauftrags aus seinen Einzelfahrten ableiten.
//
// Ein Dauerauftrag ist in Firestore kein eigenes Dokument: RideForm expandiert
// das Muster (Wochentage, Uhrzeit, Zeitraum) beim Anlegen sofort in einzelne
// Fahrten mit gemeinsamer recurringOrderId — das Muster selbst wird nirgends
// gespeichert. Rhythmus und Laufzeit lassen sich deshalb nur zurueckrechnen.
// Liste und Bearbeiten-Formular teilen sich diese Rechnung hier, damit beide
// dieselbe Serie beschreiben.

// Fahrten, die noch stattfinden sollen.
export const OPEN_RIDE_STATUSES = ['scheduled', 'pre_accepted'];
// Zusaetzlich pausierte Termine: gehoeren zur Serie, laufen aber nicht an.
export const PLANNED_RIDE_STATUSES = [...OPEN_RIDE_STATUSES, 'paused'];

// 0 = Sonntag (Date.getDay()). Reihenfolge wie im Bearbeiten-Formular.
export const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Sonntag', short: 'So' },
  { value: 1, label: 'Montag', short: 'Mo' },
  { value: 2, label: 'Dienstag', short: 'Di' },
  { value: 3, label: 'Mittwoch', short: 'Mi' },
  { value: 4, label: 'Donnerstag', short: 'Do' },
  { value: 5, label: 'Freitag', short: 'Fr' },
  { value: 6, label: 'Samstag', short: 'Sa' }
];

export const toRideDate = (value) => {
  if (!value) return null;
  const date = value?.toDate
    ? value.toDate()
    : (value?.seconds !== undefined ? new Date(value.seconds * 1000) : new Date(value));
  return Number.isNaN(date?.getTime?.()) ? null : date;
};

const rideTime = (ride) => toRideDate(ride?.scheduledTime);

/** Fahrten der Serie, die in der Zukunft liegen — aufsteigend sortiert. */
export const getFutureRides = (order, { statuses = OPEN_RIDE_STATUSES, now = Date.now() } = {}) =>
  (order?.rides || [])
    .filter(ride => statuses.includes(ride.status))
    .filter(ride => {
      const date = rideTime(ride);
      return date && date.getTime() > now;
    })
    .sort((a, b) => rideTime(a) - rideTime(b));

export const deriveWeekdays = (rides = []) => [
  ...new Set(rides.map(ride => rideTime(ride)?.getDay()).filter(day => day !== undefined))
];

/** "Mo·Mi·Fr", Montag zuerst; Sonntag ans Ende. */
export const formatWeekdays = (weekdays = []) => {
  const unique = [...new Set(weekdays)].filter(day => day !== undefined && day !== null);
  if (unique.length === 0) return null;
  if (unique.length === 7) return 'Täglich';
  return unique
    .sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7))
    .map(day => WEEKDAY_OPTIONS[day]?.short)
    .filter(Boolean)
    .join('·');
};

export const formatRideTime = (date) => (
  date ? date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : null
);

/** "Do, 07.08." — mit Wochentag, weil das Datum allein den Rhythmus nicht zeigt. */
export const formatRideDay = (date) => (
  date ? date.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' }) : null
);

export const formatRideDayWithYear = (date) => (
  date ? date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }) : null
);

/**
 * Fahrten, deren Fahrttyp von der Referenzfahrt abweicht oder die beide Flags
 * tragen (Altdaten vor der Exklusivitaets-Regel Krankenfahrt/Rechnungsfahrt).
 */
export const getFlagMismatchRides = (rides = [], referenceRide) => {
  if (!referenceRide) return [];
  return rides.filter(ride => (
    (!!ride.isKrankenfahrt && !!ride.isRechnungsfahrt) ||
    !!ride.isKrankenfahrt !== !!referenceRide.isKrankenfahrt ||
    !!ride.isRechnungsfahrt !== !!referenceRide.isRechnungsfahrt
  ));
};

/**
 * Alle Eckdaten einer Serie fuer die Uebersicht: Rhythmus, Laufzeit, Fahrttyp
 * und die Punkte, die Aufmerksamkeit brauchen (fehlende Fahrer, offene
 * Nachweise, uneinheitliche Fahrttypen).
 */
export const getRecurringOrderInfo = (order, now = Date.now()) => {
  const allRides = order?.rides || [];
  const plannedRides = getFutureRides(order, { statuses: PLANNED_RIDE_STATUSES, now });
  const openRides = plannedRides.filter(ride => OPEN_RIDE_STATUSES.includes(ride.status));

  // Referenz ist die naechste anstehende Fahrt: sie traegt den aktuellen Stand
  // der Serie. Fallback auf die erste Fahrt ueberhaupt, damit auch abgelaufene
  // Serien noch beschrieben werden.
  const referenceRide = openRides[0] || plannedRides[0] || allRides[0] || null;

  const weekdays = deriveWeekdays(plannedRides.length > 0 ? plannedRides : allRides);
  const times = [...new Set(plannedRides.map(ride => formatRideTime(rideTime(ride))).filter(Boolean))];
  const referenceTimeLabel = formatRideTime(rideTime(referenceRide));

  const lastPlannedRide = plannedRides[plannedRides.length - 1] || null;

  const mismatchRides = getFlagMismatchRides(
    plannedRides.length > 0 ? plannedRides : allRides,
    referenceRide
  );

  const medicalRides = allRides.filter(ride => ride.medicalTransportOrderId);

  return {
    referenceRide,
    weekdays,
    weekdaysLabel: formatWeekdays(weekdays),
    timeLabel: referenceTimeLabel,
    // Bei Serien mit verschobenen Einzelterminen beschreibt eine Uhrzeit die
    // Serie nicht mehr vollstaendig — das muss die Anzeige kenntlich machen.
    hasMixedTimes: times.length > 1,
    times,
    nextRideDate: rideTime(openRides[0]),
    lastRideDate: rideTime(lastPlannedRide),
    plannedCount: plannedRides.length,
    openCount: openRides.length,
    pausedCount: plannedRides.length - openRides.length,
    // Fahrten ohne Fahrer zaehlen nur solange sie noch besetzt werden koennen.
    unassignedCount: openRides.filter(ride => !ride.driverId && !ride.driverName).length,
    // Offene Nachweise nur bei bereits gefahrenen Krankenfahrten — vorher ist
    // "fehlt" der Normalzustand.
    proofOpenCount: medicalRides.filter(ride => (
      ride.status === 'completed' &&
      !['complete', 'waived'].includes(ride.medicalTransportProofStatus || 'missing')
    )).length,
    mixedFlags: mismatchRides.length > 0
      ? { count: mismatchRides.length, total: (plannedRides.length > 0 ? plannedRides : allRides).length }
      : null,
    rideType: {
      isKrankenfahrt: !!referenceRide?.isKrankenfahrt,
      isRechnungsfahrt: !!referenceRide?.isRechnungsfahrt,
      invoiceCustomerName: referenceRide?.invoiceCustomerName || null,
      isFestpreis: !!referenceRide?.isFestpreis,
      festpreisEur: referenceRide?.festpreisEur ?? null,
      isPhoneCustomer: !!referenceRide?.isPhoneCustomer,
      customerPhone: referenceRide?.customerPhone || null
    }
  };
};
